<template>
  <div>
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow-sm border-bottom" id="navbar-main">
      <div class="container-fluid">
        <div>
          <p class="text-uppercase text-muted mb-1 super-page-kicker">Configuration</p>
          <h2 class="h3 mb-0">Rate Management</h2>
        </div>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8 pb-5">
      <div class="card shadow-sm border-0 mb-4">
        <div class="card-body py-4">
          <div class="row align-items-end">
            <div class="col-lg-7 mb-3 mb-lg-0">
              <p class="super-page-kicker mb-2">Pricing Rules</p>
              <h3 class="mb-2">Maintain the rates used by billing</h3>
              <p class="text-muted mb-0">
                Manage fixed and variable pricing structures from a single screen.
              </p>
            </div>
            <div class="col-lg-5">
              <div class="row align-items-end">
                <div class="col-md-8 mb-3 mb-md-0">
                  <label class="form-control-label">Search</label>
                  <b-form-input v-model.trim="searchTerm" placeholder="Rate name"></b-form-input>
                </div>
                <div class="col-md-4 text-md-right">
                  <b-button variant="primary" class="btn-block" @click="openAddRate">
                    Add Rate
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border-0">
        <div class="card-header bg-white border-0 py-3">
          <div class="d-flex justify-content-between align-items-center">
            <h3 class="mb-0">Rate List</h3>
            <span class="badge badge-pill badge-primary">{{ filteredRates.length }} visible</span>
          </div>
        </div>

        <div v-if="isBusy" class="text-center py-6">
          <b-spinner variant="primary" class="mb-3"></b-spinner>
          <p class="text-muted mb-0">Loading rates...</p>
        </div>

        <div v-else-if="filteredRates.length" class="table-responsive">
          <b-table class="mb-0" striped hover :items="filteredRates" :fields="fields">
            <template slot="index" slot-scope="data">
              {{ data.index + (rateList.from || 1) }}
            </template>
            <template slot="method" slot-scope="data">
              <span :class="data.value === 'fixed_rate' ? 'badge badge-info' : 'badge badge-warning'">
                {{ data.value === 'fixed_rate' ? 'Fixed Rate' : 'Variable Rate' }}
              </span>
            </template>
            <template slot="tier_scope" slot-scope="data">
              <span :class="data.item.tier_scope === 'building' ? 'badge badge-success' : 'badge badge-secondary'">
                {{ data.item.tier_scope === 'building' ? 'Building' : 'Per lot' }}
              </span>
            </template>
            <template slot="actions" slot-scope="data">
              <div class="d-flex justify-content-center align-items-center">
                <b-button size="sm" variant="outline-primary" class="mr-2" @click="editRate(data.item)">
                  Edit
                </b-button>
                <b-button
                  size="sm"
                  :variant="data.item.status ? 'outline-danger' : 'outline-success'"
                  @click="changeStatus(data.item)">
                  {{ data.item.status ? 'Disable' : 'Enable' }}
                </b-button>
              </div>
            </template>
          </b-table>
          <div class="card-footer border-0 bg-white">
            <b-pagination
              align="right"
              @input="pageClick"
              :total-rows="rateList.total || 0"
              v-model="currentPage"
              :per-page="10">
            </b-pagination>
          </div>
        </div>

        <div v-else class="text-center py-6">
          <h4 class="mb-2">No rates found</h4>
          <p class="text-muted mb-0">Try another search term or add a new rate.</p>
        </div>
      </div>
    </div>

    <b-modal no-close-on-backdrop no-close-on-esc centered size="lg" hide-footer ref="addRate" id="addRate"
      :title="modalTitle" :hide-header-close="true">
      <b-form>
        <div v-if="validationMessage" class="alert alert-warning">
          {{ validationMessage }}
        </div>

        <div class="row m-0">
          <b-form-group id="name" label="Name" label-for="name" class="col-md-6">
            <b-form-input id="name" type="text" v-model="form.name" placeholder="Enter rate name"
              :class="{ 'is-invalid': error.name }">
            </b-form-input>
          </b-form-group>
          <b-form-group id="details" label="Details" label-for="details" class="col-md-6">
            <b-form-textarea id="details" type="text" v-model="form.details" placeholder="Enter details"
              :class="{ 'is-invalid': error.details }">
            </b-form-textarea>
          </b-form-group>
        </div>

        <div class="row m-0">
          <b-form-group id="calcMethod" label="Calculation Method" label-for="calcMethod" class="col-md-6">
            <b-form-select id="calcMethod" :options="method" v-model="form.method" :class="{ 'is-invalid': error.method }">
            </b-form-select>
          </b-form-group>
          <b-form-group v-if="form.method === 'fixed_rate'" id="fixed_rate" label="Fixed Rate" label-for="fixed_rate"
            class="col-md-6">
            <b-form-input id="fixed_rate" type="number" v-model="form.fixed_rate" placeholder="Enter fixed rate"
              :class="{ 'is-invalid': error.fixed_rate }">
            </b-form-input>
          </b-form-group>
        </div>

        <div class="row m-0">
          <b-form-group id="tierScope" label="Billing Scope" label-for="tierScope" class="col-md-6"
            description="Building: bill the whole building on this tariff, then split by blended rate.">
            <b-form-select id="tierScope" :options="tierScopeOptions" v-model="form.tier_scope">
            </b-form-select>
          </b-form-group>
          <b-form-group id="minimumMode" label="Minimum Charge Mode" label-for="minimumMode" class="col-md-6"
            description="Optional. Only takes effect on a building-scope tariff.">
            <b-form-select id="minimumMode" :options="minimumModeOptions" v-model="form.minimum_mode"
              :class="{ 'is-invalid': error.minimum_mode }">
            </b-form-select>
          </b-form-group>
        </div>

        <div class="row m-0" v-if="form.minimum_mode">
          <b-form-group id="minimumCharge" label="Minimum Charge" label-for="minimumCharge" class="col-md-6">
            <b-form-input id="minimumCharge" type="number" min="0" step="0.01" v-model="form.minimum_charge"
              placeholder="e.g. 1234" :class="{ 'is-invalid': error.minimum_charge }">
            </b-form-input>
          </b-form-group>
          <b-form-group v-if="form.minimum_mode === 'block'" id="minimumUnits" label="Minimum Units (covered by the minimum)"
            label-for="minimumUnits" class="col-md-6">
            <b-form-input id="minimumUnits" type="number" min="0" step="any" v-model="form.minimum_units"
              placeholder="e.g. 33" :class="{ 'is-invalid': error.minimum_units }">
            </b-form-input>
          </b-form-group>
        </div>

        <div class="row m-0" v-if="form.method === 'variable_rate'">
          <b-form-group id="vr_from" label="From" label-for="vr_from" class="col-md-3">
            <b-form-input id="vr_from" type="number" v-model="variable.from" placeholder="Enter from value"
              :class="{ 'is-invalid': variableError.from || error.variableRate }">
            </b-form-input>
          </b-form-group>
          <b-form-group id="vr_to" label="To" label-for="vr_to" class="col-md-3">
            <b-form-input id="vr_to" type="number" v-model="variable.to" placeholder="Enter to value"
              :class="{ 'is-invalid': variableError.to || error.variableRate }">
            </b-form-input>
          </b-form-group>
          <b-form-group id="vr_price" label="Price" label-for="vr_price" class="col-md-3">
            <b-form-input id="vr_price" type="number" v-model="variable.price" placeholder="Enter price"
              :class="{ 'is-invalid': variableError.price || error.variableRate }">
            </b-form-input>
          </b-form-group>
          <div class="col-md-3 pt-2">
            <b-button v-if="!variableRateEditFlag" @click="addVariableRate(variable)" type="button" variant="success"
              class="mt-4">Add Rate</b-button>
            <b-button v-else @click="updateVariableRate(variable, variableIndex)" type="button" variant="success"
              class="mt-4">Update Rate</b-button>
          </div>
        </div>

        <div class="row m-0" v-if="form.variable_rate.length && form.method === 'variable_rate'">
          <div class="col-md-12">
            <h4 class="text-center mb-3">Variable Rates</h4>
            <b-table striped hover :items="form.variable_rate" :fields="variableRateList">
              <template slot="index" slot-scope="data">
                {{ data.index + 1 }}
              </template>
              <template slot="action" slot-scope="data">
                <b-link @click="editVariableRate(data, data.index)" class="text-primary mr-3">
                  <i class="fas fa-edit"></i>
                </b-link>
                <b-link @click="delVariableRate(data.index)" class="text-danger">
                  <i class="fas fa-trash"></i>
                </b-link>
              </template>
            </b-table>
          </div>
        </div>

        <div class="row m-0">
          <div class="col-md-12 text-right">
            <b-link @click="hideModal">Cancel</b-link>
            <b-button @click="addNewRate(form)" type="button" variant="success" class="ml-3">Submit</b-button>
          </div>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import userData from '@/pages/Common/userData';

export default {
  components: {
    userData
  },
  data() {
    return {
      modalTitle: 'Add Rate',
      editFlag: false,
      variableRateEditFlag: false,
      currentPage: 1,
      totalPageCount: 1,
      isBusy: false,
      searchTerm: '',
      validationMessage: '',
      variableIndex: null,
      form: {
        name: '',
        details: '',
        method: '',
        service_charge: null,
        is_load_based: false,
        load_threshold: null,
        fixed_rate: null,
        tier_scope: 'per_lot',
        minimum_mode: '',
        minimum_charge: null,
        minimum_units: null,
        variable_rate: []
      },
      tierScopeOptions: [
        { value: 'per_lot', text: 'Per lot' },
        { value: 'building', text: 'Building' }
      ],
      minimumModeOptions: [
        { value: '', text: 'No minimum' },
        { value: 'block', text: 'Block (minimum covers the first N units)' },
        { value: 'floor', text: 'Floor (bill is at least the minimum)' }
      ],
      variable: {
        from: null,
        to: null,
        price: null
      },
      method: [
        { value: 'fixed_rate', text: 'Fixed Rate' },
        { value: 'variable_rate', text: 'Variable Rate' }
      ],
      variableRateList: [
        { key: 'index', label: 'S.No', sortable: false, class: 'text-center w-10' },
        { key: 'from', label: 'From', sortable: false, class: 'text-center' },
        { key: 'to', label: 'To', sortable: false, class: 'text-center w-25' },
        { key: 'price', label: 'Price', sortable: false, class: 'text-center w-25' },
        { key: 'action', label: 'Action', sortable: false, class: 'text-center w-25' }
      ],
      fields: [
        { key: 'index', label: 'S.No', sortable: true, class: 'text-center w-10' },
        { key: 'name', label: 'Name', sortable: true, class: 'text-center' },
        { key: 'method', label: 'Type', sortable: false, class: 'text-center w-25' },
        { key: 'tier_scope', label: 'Billing Scope', sortable: false, class: 'text-center' },
        { key: 'actions', label: 'Actions', sortable: false, class: 'text-center w-25' }
      ],
      errorFlag: false,
      error: {},
      variableError: {}
    }
  },
  computed: {
    ...mapGetters('rateManagement', ['rateList']),
    filteredRates() {
      const rateData = this.rateList && this.rateList.data ? this.rateList.data : []
      const searchTerm = this.searchTerm.toLowerCase()
      if (!searchTerm) {
        return rateData
      }
      return rateData.filter(item => (item.name || '').toLowerCase().includes(searchTerm))
    }
  },
  methods: {
    ...mapActions('rateManagement', ['RATE_LIST', 'ADD_RATE', 'UPDATE_RATE', 'RATE_STATUS_CHANGE']),
    getRateList(page = this.currentPage) {
      this.isBusy = true
      this.RATE_LIST(page)
        .then(() => {
          this.currentPage = page
          this.totalPageCount = Math.ceil((this.rateList.total || 0) / 10)
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    changeStatus(data) {
      const actionLabel = data.status ? 'disable' : 'enable'
      const confirmed = window.confirm(`Do you want to ${actionLabel} ${data.name}?`)

      if (!confirmed) {
        return
      }

      this.RATE_STATUS_CHANGE({ rate_management_id: data.id })
        .then(() => {
          this.editFlag = false
          this.clearForm()
          this.getRateList(this.currentPage)
        })
        .catch(() => {})
    },
    validateVariableRate(data) {
      this.variableError = {
        from: false,
        to: false,
        price: false
      }
      this.errorFlag = false
      const validationErrors = []

      if (!data.from && data.from !== 0) {
        validationErrors.push('Enter a variable-rate start value.')
        this.errorFlag = true
        this.variableError.from = true
      }
      if (!data.to && data.to !== 0) {
        validationErrors.push('Enter a variable-rate end value.')
        this.errorFlag = true
        this.variableError.to = true
      }
      if (!data.price && data.price !== 0) {
        validationErrors.push('Enter a variable-rate price.')
        this.errorFlag = true
        this.variableError.price = true
      }

      this.validationMessage = validationErrors[0] || ''
    },
    validateRateForm(data) {
      this.errorFlag = false
      this.error = {
        name: false,
        details: false,
        method: false,
        fixed_rate: false,
        variableRate: false,
        loadThreshold: false,
        minimum_mode: false,
        minimum_charge: false,
        minimum_units: false
      }

      const validationErrors = []

      if (!data.name) {
        validationErrors.push('Enter a rate name.')
        this.errorFlag = true
        this.error.name = true
      }
      if (!data.details) {
        validationErrors.push('Enter rate details.')
        this.errorFlag = true
        this.error.details = true
      }
      if (!data.method) {
        validationErrors.push('Select a calculation method.')
        this.errorFlag = true
        this.error.method = true
      }
      if (data.method === 'fixed_rate' && !data.fixed_rate && data.fixed_rate !== 0) {
        validationErrors.push('Enter a fixed rate value.')
        this.errorFlag = true
        this.error.fixed_rate = true
      }
      if (data.method === 'variable_rate' && this.form.variable_rate.length === 0) {
        validationErrors.push('Add at least one variable rate.')
        this.errorFlag = true
        this.error.variableRate = true
      }

      if (data.minimum_mode) {
        if (data.minimum_charge === '' || data.minimum_charge === null || data.minimum_charge === undefined || Number(data.minimum_charge) < 0) {
          validationErrors.push('Enter a minimum charge of 0 or more.')
          this.errorFlag = true
          this.error.minimum_charge = true
        }
        if (data.minimum_mode === 'block' &&
          (data.minimum_units === '' || data.minimum_units === null || data.minimum_units === undefined || Number(data.minimum_units) < 0)) {
          validationErrors.push('Enter the minimum units (required for a block minimum).')
          this.errorFlag = true
          this.error.minimum_units = true
        }
      }

      this.validationMessage = validationErrors[0] || ''
    },
    addVariableRate(data) {
      this.validateVariableRate(data)
      if (!this.errorFlag) {
        this.form.variable_rate.push({
          from: data.from,
          to: data.to,
          price: data.price
        })
        this.variable = { from: null, to: null, price: null }
        this.validationMessage = ''
      }
    },
    editVariableRate(data, index) {
      this.variableRateEditFlag = true
      this.variable = { ...data.item }
      this.variableIndex = index
    },
    updateVariableRate(data, index) {
      this.validateVariableRate(data)
      if (!this.errorFlag) {
        this.form.variable_rate.splice(index, 1, {
          from: data.from,
          to: data.to,
          price: data.price
        })
        this.variable = { from: null, to: null, price: null }
        this.variableRateEditFlag = false
        this.variableIndex = null
        this.validationMessage = ''
      }
    },
    delVariableRate(index) {
      this.form.variable_rate.splice(index, 1)
    },
    addNewRate(data) {
      this.validateRateForm(data)
      if (!this.errorFlag) {
        const action = this.editFlag ? this.UPDATE_RATE : this.ADD_RATE
        const payload = this.buildRatePayload()
        if (this.editFlag) {
          payload.rate_management_id = data.id
        }

        action(payload)
          .then(() => {
            this.clearForm()
            this.hideModal()
            this.getRateList(this.currentPage)
          })
          .catch(() => {})
      }
    },
    // Builds the save payload; minimum fields are sent all together or not at all, per the backend contract. 2026-10-25
    buildRatePayload() {
      const payload = { ...this.form }
      if (!payload.tier_scope) payload.tier_scope = 'per_lot'
      if (payload.minimum_mode) {
        payload.minimum_charge = Number(payload.minimum_charge)
        if (payload.minimum_mode === 'block') {
          payload.minimum_units = Number(payload.minimum_units)
        } else {
          delete payload.minimum_units
        }
      } else {
        delete payload.minimum_mode
        delete payload.minimum_charge
        delete payload.minimum_units
      }
      return payload
    },
    editRate(data) {
      this.editFlag = true
      this.modalTitle = 'Edit Rate'
      this.validationMessage = ''
      this.form = {
        ...data,
        tier_scope: data.tier_scope || 'per_lot',
        minimum_mode: data.minimum_mode || '',
        minimum_charge: data.minimum_charge === undefined ? null : data.minimum_charge,
        minimum_units: data.minimum_units === undefined ? null : data.minimum_units,
        fixed_rate: data.method === 'fixed_rate' && data.variable_rate && data.variable_rate.length
          ? data.variable_rate[0].price
          : null,
        variable_rate: data.method === 'variable_rate' && data.variable_rate ? [...data.variable_rate] : []
      }
      this.$refs.addRate.show()
    },
    openAddRate() {
      this.hideModal()
      this.$refs.addRate.show()
    },
    hideModal() {
      this.modalTitle = 'Add Rate'
      this.editFlag = false
      this.variableRateEditFlag = false
      this.variableIndex = null
      this.validationMessage = ''
      this.clearForm()
      if (this.$refs.addRate) {
        this.$refs.addRate.hide()
      }
    },
    pageClick(page) {
      this.getRateList(page)
    },
    clearForm() {
      this.form = {
        name: '',
        details: '',
        method: '',
        service_charge: null,
        is_load_based: false,
        load_threshold: null,
        fixed_rate: null,
        tier_scope: 'per_lot',
        minimum_mode: '',
        minimum_charge: null,
        minimum_units: null,
        variable_rate: []
      }
      this.variable = { from: null, to: null, price: null }
      this.error = {}
      this.variableError = {}
    }
  },
  created() {
    this.getRateList(1)
  }
}
</script>

<style scoped>
.super-page-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #5e72e4;
}
</style>



// WEBPACK FOOTER //
// src/pages/SuperAdmin/rateManagement.vue